/**
 * Attachment Viewer modal for Ragsmith Web UI
 * Displays full content of pasted attachments
 */

import { mediaUrl } from './api.js';

/** Modal state */
let modalOpen = false;
let currentAttachment = null;

/** Modal DOM elements */
let modalElements = {
    overlay: null,
    panel: null,
    closeBtn: null,
    title: null,
    content: null,
    copyBtn: null
};

/**
 * Format attachment size for display
 */
function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Initialize Attachment Viewer
 */
export function initAttachmentViewer() {
    // Create modal HTML
    createModalHTML();

    // Get modal elements
    modalElements.overlay = document.getElementById('attachment-viewer-overlay');
    modalElements.panel = document.getElementById('attachment-viewer-panel');
    modalElements.closeBtn = document.getElementById('attachment-viewer-close');
    modalElements.title = document.getElementById('attachment-viewer-title');
    modalElements.content = document.getElementById('attachment-viewer-content');
    modalElements.copyBtn = document.getElementById('attachment-copy-btn');

    // Event listeners
    modalElements.overlay?.addEventListener('click', closeAttachmentViewer);
    modalElements.closeBtn?.addEventListener('click', closeAttachmentViewer);
    modalElements.copyBtn?.addEventListener('click', copyAttachmentContent);

    // Close on Escape
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOpen) {
            closeAttachmentViewer();
        }
    });

    // Expose globally for ui.js click handler
    window.openAttachmentViewer = openAttachmentViewer;
    window.openVideoAnalysis = openVideoAnalysis;
}

/**
 * Show the transcript + frame descriptions a video was reduced to.
 *
 * Same shell as the text viewer, but reading `analysis` rather than `content`
 * — a video's content is a media id, and showing that would be useless. This
 * is how you tell a bad frame sample from a bad answer.
 */
export function openVideoAnalysis(attachment) {
    if (!attachment?.analysis) return;
    openAttachmentViewer({
        ...attachment,
        name: `What the model saw — ${attachment.name || 'video'}`,
        content: attachment.analysis,
        size: attachment.analysis.length,
    });
}

/**
 * Create the modal HTML structure
 */
function createModalHTML() {
    const modalHTML = `
        <div id="attachment-viewer-overlay" class="modal-overlay"></div>
        <div id="attachment-viewer-panel" class="attachment-viewer-panel">
            <div class="attachment-viewer-header">
                <h3 id="attachment-viewer-title">Attachment</h3>
                <button id="attachment-viewer-close" class="modal-close">&times;</button>
            </div>
            <div id="attachment-viewer-content" class="attachment-viewer-content">
                <pre><code></code></pre>
            </div>
            <div class="attachment-viewer-footer">
                <button id="attachment-copy-btn" class="menu-btn">Copy to Clipboard</button>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHTML);
}

/**
 * Open the attachment viewer modal
 */
export function openAttachmentViewer(attachment) {
    if (!attachment) return;

    currentAttachment = attachment;

    // Set title with name and size
    if (modalElements.title) {
        modalElements.title.textContent = `${attachment.name} (${formatSize(attachment.size)})`;
    }

    // Set content. An image is shown as an image: the chips route images to
    // the gallery, but anything that still lands here must not be dumped
    // into a code block, which for a saved image (bytes by id, not inline)
    // was an empty box under the file's name.
    if (modalElements.content) {
        const isImage = attachment.type === 'image' && (attachment.content || attachment.media_id);
        const pre = modalElements.content.querySelector('pre');
        let img = modalElements.content.querySelector('img.attachment-viewer-image');
        if (isImage) {
            if (!img) {
                img = document.createElement('img');
                img.className = 'attachment-viewer-image';
                modalElements.content.appendChild(img);
            }
            img.alt = attachment.name || 'Image';
            img.src = attachment.content
                ? `data:${attachment.media_type || 'image/jpeg'};base64,${attachment.content}`
                : mediaUrl(attachment.media_id, 'stream');
            img.hidden = false;
            if (pre) pre.hidden = true;
        } else {
            if (img) img.hidden = true;
            if (pre) pre.hidden = false;
            const codeEl = modalElements.content.querySelector('code');
            if (codeEl) codeEl.textContent = attachment.content;
        }
        // Copying an image as text would paste nothing useful.
        if (modalElements.copyBtn) modalElements.copyBtn.hidden = !!isImage;
    }

    // Show modal
    modalOpen = true;
    modalElements.overlay?.classList.add('active');
    modalElements.panel?.classList.add('open');
    document.body.style.overflow = 'hidden';
}

/**
 * Close the attachment viewer modal
 */
export function closeAttachmentViewer() {
    modalOpen = false;
    modalElements.overlay?.classList.remove('active');
    modalElements.panel?.classList.remove('open');
    document.body.style.overflow = '';
    currentAttachment = null;
}

/**
 * Copy attachment content to clipboard
 */
async function copyAttachmentContent() {
    if (!currentAttachment) return;

    try {
        await navigator.clipboard.writeText(currentAttachment.content);

        // Show brief feedback
        const originalText = modalElements.copyBtn.textContent;
        modalElements.copyBtn.textContent = 'Copied!';
        modalElements.copyBtn.classList.add('success');

        setTimeout(() => {
            modalElements.copyBtn.textContent = originalText;
            modalElements.copyBtn.classList.remove('success');
        }, 1500);
    } catch (err) {
        console.error('Failed to copy:', err);
        modalElements.copyBtn.textContent = 'Failed to copy';
        setTimeout(() => {
            modalElements.copyBtn.textContent = 'Copy to Clipboard';
        }, 1500);
    }
}
